import React, { useState } from 'react';
import * as R from 'ramda';
import { useListContext } from 'react-admin';
import {
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { green, orange, red } from '@material-ui/core/colors';
import { KeyboardArrowDown, KeyboardArrowRight } from '@material-ui/icons';
import moment from 'moment';

import { QuizTitle } from 'resources/common';
import QuizSubmissionDetail from 'resources/common/QuizSubmissionDetail';

const COLUMN_COUNT = 5;

const useStyles = makeStyles({
  score: { width: 100 },
  status: { width: 110 },
  date: { width: 180, whiteSpace: 'nowrap' },
  expandCell: {
    width: 48,
    paddingLeft: 0,
    paddingRight: 8,
    textAlign: 'right',
  },
  row: {
    cursor: 'pointer',
  },
  detailCell: {
    paddingTop: 0,
    paddingBottom: 0,
    backgroundColor: '#fafafa',
  },
  pending: {
    padding: '16px 24px',
    color: '#666',
    fontSize: '0.875rem',
  },
});

const getScoreChipStyle = (score) => {
  if (score >= 70) {
    return { color: 'white', backgroundColor: green[700] };
  }
  if (score >= 40) {
    return { color: 'white', backgroundColor: orange[700] };
  }
  return { color: 'white', backgroundColor: red[700] };
};

const StatusField = ({ record }) =>
  record.student_submission ? 'Submitted' : 'Pending';

const ScoreField = ({ record, className }) => {
  const score = R.prop('student_score', record);
  if (!Number.isFinite(score)) return null;
  return (
    <Chip
      className={className}
      label={`${Math.round(score)}%`}
      style={getScoreChipStyle(score)}
    />
  );
};

const SubmissionDateField = ({ record }) => {
  const submission = record.student_submission;
  const submissionDate = submission?.submittedAt || submission?.created_at;
  if (!submissionDate) return null;
  return <span>{moment(submissionDate).format('DD/MM/YYYY, h:mm A')}</span>;
};

const ExpandableStudentQuizTable = () => {
  const classes = useStyles();
  const { data, ids, currentSort, setSort } = useListContext();
  const [expandedQuizIds, setExpandedQuizIds] = useState([]);

  const handleSort = (field) => {
    const order =
      currentSort.field === field && currentSort.order === 'ASC' ? 'DESC' : 'ASC';
    setSort(field, order);
  };

  const toggleQuiz = (quizId) => {
    setExpandedQuizIds((current) =>
      current.includes(quizId)
        ? current.filter((id) => id !== quizId)
        : [...current, quizId]
    );
  };

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Quiz</TableCell>
          <TableCell className={classes.status}>Status</TableCell>
          <TableCell
            className={classes.score}
            sortDirection={
              currentSort.field === 'student_score'
                ? currentSort.order.toLowerCase()
                : false
            }
          >
            <TableSortLabel
              active={currentSort.field === 'student_score'}
              direction={
                currentSort.field === 'student_score'
                  ? currentSort.order.toLowerCase()
                  : 'asc'
              }
              onClick={() => handleSort('student_score')}
            >
              Score
            </TableSortLabel>
          </TableCell>
          <TableCell className={classes.date}>Submission Date</TableCell>
          <TableCell className={classes.expandCell} />
        </TableRow>
      </TableHead>
      <TableBody>
        {ids.map((quizId) => {
          const record = data[quizId];
          if (!record) return null;
          const isExpanded = expandedQuizIds.includes(quizId);
          const submission = record.student_submission;
          const canShowDetail = Boolean(submission?.submissionId);

          return (
            <React.Fragment key={quizId}>
              <TableRow
                hover
                className={classes.row}
                onClick={() => toggleQuiz(quizId)}
              >
                <TableCell>
                  <QuizTitle record={record} source="path" />
                </TableCell>
                <TableCell className={classes.status}>
                  <StatusField record={record} />
                </TableCell>
                <TableCell className={classes.score}>
                  <ScoreField record={record} />
                </TableCell>
                <TableCell className={classes.date}>
                  <SubmissionDateField record={record} />
                </TableCell>
                <TableCell className={classes.expandCell}>
                  {isExpanded ? <KeyboardArrowDown /> : <KeyboardArrowRight />}
                </TableCell>
              </TableRow>
              {isExpanded && (
                <TableRow>
                  <TableCell className={classes.detailCell} colSpan={COLUMN_COUNT}>
                    {canShowDetail ? (
                      <QuizSubmissionDetail
                        record={submission}
                        quiz={record}
                      />
                    ) : (
                      <Box className={classes.pending}>
                        <Typography>
                          This student has not submitted this quiz yet.
                        </Typography>
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              )}
            </React.Fragment>
          );
        })}
      </TableBody>
    </Table>
  );
};

export default ExpandableStudentQuizTable;
